// router.js — SPA 路由器（hami 版，桌面/移动双入口）
// 路径匹配 → 动态 import chunk → 更新 #pageSlot 内容区 → 初始化
// 静态路由：/home /project /archive /files 及 -m 版本
// 动态路由：/project/:id 及 /project-m/:id（参数经 initApp(params) 传给页面入口）
// 框架（侧边栏/顶部栏+底部导航）由 shell-init 只注入一次，路由切换不重建

import { ensureShell, setPageSlot } from './shell-init.js';

// ===== 路由表 =====
var ROUTES = {
  '/home':       { chunk: function() { return import('../public/assets/js/esm/pages/index.js'); }, chunkId: 'home', title: '哈密' },
  '/home-m':     { chunk: function() { return import('../public/assets/js/esm/pages/index.js'); }, chunkId: 'home', title: '哈密' },
  '/project':    { chunk: function() { return import('../public/assets/js/esm/pages/project.js'); }, chunkId: 'project', title: '项目 - 哈密' },
  '/project-m':  { chunk: function() { return import('../public/assets/js/esm/pages/project.js'); }, chunkId: 'project', title: '项目 - 哈密' },
  '/archive':    { chunk: function() { return import('../public/assets/js/esm/pages/archive.js'); }, chunkId: 'archive', title: '资料 - 哈密' },
  '/archive-m':  { chunk: function() { return import('../public/assets/js/esm/pages/archive.js'); }, chunkId: 'archive', title: '资料 - 哈密' },
  '/files':      { chunk: function() { return import('../public/assets/js/esm/pages/files.js'); }, chunkId: 'files', title: '文件 - 哈密' },
  '/files-m':    { chunk: function() { return import('../public/assets/js/esm/pages/files.js'); }, chunkId: 'files', title: '文件 - 哈密' },
};

// ===== 动态路由表（:param 前缀匹配，静态路由优先） =====
var DYNAMIC_ROUTES = [
  { pattern: /^\/project\/([^/]+)$/, chunk: function() { return import('../public/assets/js/esm/pages/project-detail.js'); }, chunkId: 'project', title: '项目详情 - 哈密', params: function(m) { return { id: decodeURIComponent(m[1]) }; } },
  { pattern: /^\/project-m\/([^/]+)$/, chunk: function() { return import('../public/assets/js/esm/pages/project-detail.js'); }, chunkId: 'project', title: '项目详情 - 哈密', params: function(m) { return { id: decodeURIComponent(m[1]) }; } },
];

// ===== 内部状态 =====
var _currentDestroy = null;
var _loading = false;

// ===== 平台识别（断点统一 1024px，对齐 lumeet） =====
var MOBILE_QUERY = '(max-width: 1024px)';
function isMobileViewport() {
  return window.matchMedia(MOBILE_QUERY).matches;
}

// workspace 页面段（可加 -m 后缀的业务页）
var WORKSPACE_SEGMENTS = ['home', 'project', 'archive', 'files'];

// 解析路径：若是 workspace 业务页返回 { base, mobile, rest }，否则 null
function parseWorkspace(pathname) {
  var clean = (pathname || '').replace(/\/$/, '') || '';
  var segs = clean.split('/');
  var page = segs[1] || '';
  if (!page) return null;
  var base = page.replace(/-m$/, '');
  if (WORKSPACE_SEGMENTS.indexOf(base) === -1) return null;
  return { base: base, mobile: page.slice(-2) === '-m', rest: segs.slice(2) };
}

// 按目标平台加/去 -m 后缀（作用于首段，兼容动态路由 /project/:id）
function withPlatform(pathname, wantMobile) {
  var info = parseWorkspace(pathname);
  if (!info || info.mobile === wantMobile) return pathname;
  var page = info.base + (wantMobile ? '-m' : '');
  var out = '/' + page + (info.rest.length ? '/' + info.rest.join('/') : '');
  return out;
}

// 初始加载时按屏幕自动重定向到对应平台；返回 true 表示已跳转（调用方需中断后续渲染）
export function redirectPlatform() {
  var pathname = location.pathname;
  var info = parseWorkspace(pathname);
  if (!info) return false;
  var wantMobile = isMobileViewport();
  if (info.mobile === wantMobile) return false;
  var newPath = withPlatform(pathname, wantMobile);
  var qs = location.search || '';
  var hash = location.hash || '';
  location.replace(newPath + qs + hash);
  return true;
}

// ===== 公开 API =====

export function navigate(url) {
  if (!url || url.charAt(0) === '#') return;
  if (url.indexOf('http') === 0 || url.indexOf('.html') !== -1) {
    window.location.href = url;
    return;
  }

  var clean = url.replace(/\/$/, '') || '/home';
  // 平台自动加/去 -m 后缀
  if (parseWorkspace(clean)) {
    clean = withPlatform(clean, isMobileViewport());
  }
  var route = matchRoute(clean);
  history.pushState(null, '', clean);
  if (route) {
    loadRoute(route);
  } else {
    render404();
  }
}

export function matchRoute(pathname) {
  var clean = (pathname || location.pathname).replace(/\/$/, '') || '/home';
  // 静态路由优先
  if (ROUTES[clean]) return ROUTES[clean];
  // 动态路由兜底
  for (var i = 0; i < DYNAMIC_ROUTES.length; i++) {
    var dr = DYNAMIC_ROUTES[i];
    var m = clean.match(dr.pattern);
    if (m) {
      return {
        chunk: dr.chunk,
        chunkId: dr.chunkId,
        title: dr.title,
        params: dr.params ? dr.params(m) : {}
      };
    }
  }
  return null;
}

export function loadRoute(route, force) {
  if (_loading && !force) return;

  var params = route.params || {};

  // 1. 保存旧路由（销毁）
  if (_currentDestroy) {
    try { _currentDestroy(); } catch (e) { console.error('[Router] destroy 失败:', e); }
    _currentDestroy = null;
  }

  // 2. 确保框架已注入 + 更新内容区
  ensureShell();
  setPageSlot(route.chunkId, params);

  // 3. 更新标题
  document.title = route.title;

  // 4. 高亮导航
  highlightNav(route.chunkId);

  // 5. 动态 import chunk + 初始化
  _loading = true;
  route.chunk().then(function(mod) {
    try {
      if (mod.initApp) {
        var initResult = mod.initApp(params);
        if (initResult && initResult.then) {
          initResult.then(function() { finishLoad(mod); }).catch(function(e) {
            console.error('[Router] initApp (async) 失败:', e);
            handleInitError();
          });
          return;
        }
      }
      finishLoad(mod);
    } catch (e) {
      console.error('[Router] initApp 失败:', e);
      handleInitError();
    }
  }).catch(function(err) {
    console.error('[Router] chunk 加载失败:', err);
    setPageSlotHtml('<div style="text-align:center;padding:80px 20px;"><p>页面加载失败，请检查网络连接</p></div>');
    _loading = false;
  });
}

// ===== 内部函数 =====

// 只替换 #pageSlot 内容区（保留框架），供错误/404 场景复用
function setPageSlotHtml(html) {
  var slot = document.getElementById('pageSlot');
  if (slot) slot.innerHTML = html;
}

function finishLoad(mod) {
  _currentDestroy = mod.destroyApp || null;
  _loading = false;
}

function handleInitError() {
  setPageSlotHtml('<div style="text-align:center;padding:80px 20px;"><p>页面初始化失败</p></div>');
  _loading = false;
}

function highlightNav(chunkId) {
  document.querySelectorAll('[data-nav]').forEach(function(el) {
    if (el.getAttribute('data-nav') === chunkId) {
      el.classList.add('active');
    } else {
      el.classList.remove('active');
    }
  });
}

export function render404() {
  document.title = '404 - 哈密';
  setPageSlotHtml(
    '<div style="text-align:center;padding:120px 20px;">' +
    '<h1 style="font-size:72px;color:#ddd;margin:0;">404</h1>' +
    '<p style="font-size:16px;color:#888;margin:16px 0;">页面不存在</p>' +
    '<a href="/home" data-spa-link style="color:var(--primary);">返回首页</a>' +
    '</div>'
  );
}

// ===== popstate 处理 =====
function rerenderCurrentPath(force) {
  var route = matchRoute(location.pathname);
  if (route) { loadRoute(route, force); } else { render404(); }
}

window.addEventListener('popstate', function() { rerenderCurrentPath(false); });

// 暴露 navigate 给各 chunk 使用
window.__spaNavigate = navigate;

// ===== 全局点击委托：data-spa-link 的 SPA 内导航 + 导航按钮 =====
document.addEventListener('click', function(e) {
  var target = e.target;
  while (target && target !== document) {
    if (target.tagName === 'A' && target.getAttribute('data-spa-link') !== null) {
      e.preventDefault();
      navigate(target.getAttribute('href'));
      return;
    }
    // 导航按钮（data-action="nav"）
    if (target.getAttribute && target.getAttribute('data-action') === 'nav') {
      var href = target.getAttribute('data-href');
      if (href) { navigate(href); }
      return;
    }
    target = target.parentNode;
  }
});
